import type { ResourceLayout } from "../types/chat";

/**
 * Pure layout resolver for the chat takeover. Kept free of Vue/Nuxt so the
 * decision rules can be unit-tested in isolation (B10); the reactive composable
 * `useChatLayout` snapshots its refs and delegates here.
 */

export type EffectiveLayout = "inline" | "split";

/** Inputs to the pure layout decision, snapshotted from refs by the composable. */
export interface ResolveChatLayoutInput {
  /** Measured width (px) of the takeover body. `0` means not yet measured. */
  width: number;
  /** Width (px) below which the resource is forced inline. */
  inlineBelow: number;
  /** Explicit caller override; `auto`/undefined defers to agent + width. */
  override?: ResourceLayout;
  /** The governing agent's preferred layout. */
  agentLayout?: ResourceLayout;
  /** Whether a resource is actually present to lay out. */
  hasResource: boolean;
}

/** Resolved layout decision. */
export interface ChatLayoutDecision {
  isMeasured: boolean;
  forcedInline: boolean;
  preferred: EffectiveLayout;
  layout: EffectiveLayout;
  showSplit: boolean;
  showInline: boolean;
}

/**
 * Resolves how the governing resource is laid out beside the chat takeover.
 *
 *  - below `inlineBelow` px (once measured) the resource is **forced inline**;
 *  - otherwise an explicit `override` wins, then the `agentLayout` preference,
 *    finally defaulting to `split`.
 *
 * `forcedInline` is gated on an actual measurement (`isMeasured`) so the first,
 * pre-measure frame keeps the preferred (typically `split`) layout instead of
 * flashing inline while the width is still `0`.
 */
export function resolveChatLayout(input: ResolveChatLayoutInput): ChatLayoutDecision {
  const isMeasured = input.width > 0;
  const forcedInline = isMeasured && input.width < input.inlineBelow;

  let preferred: EffectiveLayout = "split";
  if (input.override && input.override !== "auto") {
    preferred = input.override;
  } else if (input.agentLayout && input.agentLayout !== "auto") {
    preferred = input.agentLayout;
  }

  const layout: EffectiveLayout = forcedInline ? "inline" : preferred;

  return {
    isMeasured,
    forcedInline,
    preferred,
    layout,
    showSplit: input.hasResource && layout === "split",
    showInline: input.hasResource && layout === "inline",
  };
}
